import type { EvasaoEvent, EvasaoMetric, EvasaoSnapshot } from "../../types/evasao";

export const ANALYSIS_START_PERIOD = "2025/2";

export type EvasaoFilters = {
  periodo: string;
  curso: string;
  ua: string;
  motivo: string;
};

export type RankedItem = {
  label: string;
  value: number;
  share: number;
};

export type LifetimeBucket = RankedItem & {
  description: string;
};

export type TrendComparison = {
  latest: EvasaoMetric | null;
  previous: EvasaoMetric | null;
  deltaPoints: number | null;
  partialPeriod: string | null;
};

export type EvasaoSummary = {
  events: EvasaoEvent[];
  total: number;
  attendedCount: number;
  attendedRate: number;
  identifiedReasonCount: number;
  reasonCoverage: number;
  lifetimeCount: number;
  lifetimeAverageDays: number;
  lifetimeMedianDays: number;
  earlyExitCount: number;
  earlyExitRate: number;
  reasons: RankedItem[];
  courses: RankedItem[];
  uas: RankedItem[];
  periods: RankedItem[];
  lifetimeBuckets: LifetimeBucket[];
};

export const EMPTY_EVASAO_FILTERS: EvasaoFilters = {
  periodo: "",
  curso: "",
  ua: "",
  motivo: ""
};

export function periodRank(value: unknown): number {
  const match = String(value || "").trim().match(/^(\d{4})\/(1|2)$/);
  if (!match) return -1;
  return Number(match[1]) * 2 + Number(match[2]) - 1;
}

export function isAnalysisPeriod(value: unknown): boolean {
  return periodRank(value) >= periodRank(ANALYSIS_START_PERIOD);
}

export function eventPeriod(row: EvasaoEvent): string {
  return cleanText(row.periodo_letivo_analitico || row.periodo_letivo, "Não informado");
}

export function eventCourse(row: EvasaoEvent): string {
  return cleanText(row.curso_analitico || row.curso, "Não informado");
}

export function eventUa(row: EvasaoEvent): string {
  return cleanText(row.ua_analitica || row.ua, "Não informado");
}

export function eventReason(row: EvasaoEvent): string {
  return cleanText(row.motivo_cancelamento, "Sem motivo registrado");
}

export function buildEvasaoOptions(snapshot: EvasaoSnapshot) {
  const events = analysisEvents(snapshot);
  return {
    periodo: unique(events.map(eventPeriod)).sort((a, b) => periodRank(b) - periodRank(a)).map(toOption),
    curso: unique(events.map(eventCourse)).sort(localeSort).map(toOption),
    ua: unique(events.map(eventUa)).sort(localeSort).map(toOption),
    motivo: unique(events.map(eventReason)).sort((a, b) => {
      if (isMissingReason(a)) return 1;
      if (isMissingReason(b)) return -1;
      return localeSort(a, b);
    }).map(toOption)
  };
}

export function buildEvasaoSummary(snapshot: EvasaoSnapshot, filters: EvasaoFilters): EvasaoSummary {
  const events = analysisEvents(snapshot).filter((row) => (
    (!filters.periodo || eventPeriod(row) === filters.periodo)
    && (!filters.curso || eventCourse(row) === filters.curso)
    && (!filters.ua || eventUa(row) === filters.ua)
    && (!filters.motivo || eventReason(row) === filters.motivo)
  ));

  const attendedCount = events.filter(hasCancellationService).length;
  const identifiedReasonCount = events.filter((row) => !isMissingReason(eventReason(row))).length;
  const lifetimes = events
    .map((row) => numeric(row.lifetime_dias))
    .filter((value): value is number => value !== null && value >= 0);
  const earlyExitCount = lifetimes.filter((days) => days <= 90).length;

  return {
    events,
    total: events.length,
    attendedCount,
    attendedRate: ratio(attendedCount, events.length),
    identifiedReasonCount,
    reasonCoverage: ratio(identifiedReasonCount, events.length),
    lifetimeCount: lifetimes.length,
    lifetimeAverageDays: average(lifetimes),
    lifetimeMedianDays: median(lifetimes),
    earlyExitCount,
    earlyExitRate: ratio(earlyExitCount, lifetimes.length),
    reasons: rankBy(events, eventReason),
    courses: rankBy(events, eventCourse),
    uas: rankBy(events, eventUa),
    periods: rankBy(events, eventPeriod).sort((a, b) => periodRank(a.label) - periodRank(b.label)),
    lifetimeBuckets: buildLifetimeBuckets(lifetimes)
  };
}

export function fullTrend(snapshot: EvasaoSnapshot): EvasaoMetric[] {
  return [...(snapshot.analise_tendencia || [])]
    .filter((row) => periodRank(row.periodo_letivo) >= 0)
    .sort((a, b) => periodRank(a.periodo_letivo) - periodRank(b.periodo_letivo));
}

export function trendComparison(snapshot: EvasaoSnapshot): TrendComparison {
  const trend = fullTrend(snapshot);
  const partialPeriod = currentSnapshotPeriod(snapshot.data_geracao);
  const completed = partialPeriod
    ? trend.filter((row) => row.periodo_letivo !== partialPeriod)
    : trend;
  const latest = completed.at(-1) || null;
  const previous = completed.at(-2) || null;
  const latestRate = numeric(latest?.taxa_evasao);
  const previousRate = numeric(previous?.taxa_evasao);

  return {
    latest,
    previous,
    deltaPoints: latestRate === null || previousRate === null ? null : latestRate - previousRate,
    partialPeriod: trend.some((row) => row.periodo_letivo === partialPeriod) ? partialPeriod : null
  };
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat("pt-BR").format(value);
}

export function formatPercent(value: number, digits = 1): string {
  return `${value.toLocaleString("pt-BR", { minimumFractionDigits: digits, maximumFractionDigits: digits })}%`;
}

export function formatDays(value: number): string {
  return `${Math.round(value).toLocaleString("pt-BR")} dias`;
}

export function formatSnapshotDate(value: string | undefined): string {
  if (!value) return "data não informada";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "data não informada";
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}

export function formatEventDate(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("pt-BR", { timeZone: "UTC" });
}

export function searchEvents(events: EvasaoEvent[], query: string): EvasaoEvent[] {
  const needle = normalize(query);
  if (!needle) return events;
  return events.filter((row) => [
    row.ra,
    eventCourse(row),
    eventUa(row),
    eventReason(row),
    row.campus_polo
  ].some((value) => normalize(value).includes(needle)));
}

export function hasCancellationService(row: EvasaoEvent): boolean {
  return row.tem_atendimento_cancelamento === true || Number(row.qtd_atendimentos_cancelamento || 0) > 0;
}

export function activeFilterCount(filters: EvasaoFilters): number {
  return Object.values(filters).filter(Boolean).length;
}

function analysisEvents(snapshot: EvasaoSnapshot): EvasaoEvent[] {
  return (snapshot.matriculas_detalhe || []).filter((row) => isAnalysisPeriod(eventPeriod(row)));
}

function rankBy(events: EvasaoEvent[], labelFor: (row: EvasaoEvent) => string): RankedItem[] {
  const counts = new Map<string, number>();
  for (const row of events) {
    const label = labelFor(row);
    counts.set(label, (counts.get(label) || 0) + 1);
  }
  return [...counts.entries()]
    .map(([label, value]) => ({ label, value, share: ratio(value, events.length) }))
    .sort((a, b) => b.value - a.value || localeSort(a.label, b.label));
}

function buildLifetimeBuckets(values: number[]): LifetimeBucket[] {
  const buckets = [
    { label: "Até 30 dias", description: "Primeiro mês", min: 0, max: 30 },
    { label: "31 a 90 dias", description: "Até o 3º mês", min: 31, max: 90 },
    { label: "91 a 180 dias", description: "Até 6 meses", min: 91, max: 180 },
    { label: "181 a 365 dias", description: "Até 1 ano", min: 181, max: 365 },
    { label: "Mais de 1 ano", description: "Acima de 365 dias", min: 366, max: Number.POSITIVE_INFINITY }
  ];
  return buckets.map((bucket) => {
    const value = values.filter((days) => days >= bucket.min && days <= bucket.max).length;
    return {
      label: bucket.label,
      description: bucket.description,
      value,
      share: ratio(value, values.length)
    };
  });
}

function currentSnapshotPeriod(generatedAt: string | undefined): string | null {
  if (!generatedAt) return null;
  const date = new Date(generatedAt);
  if (Number.isNaN(date.getTime())) return null;
  const semester = date.getMonth() < 6 ? 1 : 2;
  return `${date.getFullYear()}/${semester}`;
}

function cleanText(value: unknown, fallback: string): string {
  const result = String(value ?? "").trim();
  return result || fallback;
}

function normalize(value: unknown): string {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("pt-BR")
    .trim();
}

function isMissingReason(value: string): boolean {
  return ["sem motivo registrado", "não informado", "nao informado", "n/a", "na"].includes(normalize(value));
}

function numeric(value: unknown): number | null {
  const result = Number(value);
  return value === null || value === undefined || value === "" || !Number.isFinite(result) ? null : result;
}

function average(values: number[]): number {
  return values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;
}

function median(values: number[]): number {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

function ratio(value: number, total: number): number {
  return total ? (value / total) * 100 : 0;
}

function unique(values: string[]): string[] {
  return [...new Set(values)];
}

function toOption(value: string) {
  return { value, label: value };
}

function localeSort(a: string, b: string): number {
  return a.localeCompare(b, "pt-BR");
}
